import {
  Dialog,
  DialogContent,
  DialogTitle,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useQuery } from "react-query";
import { ListRequestOfUserFilter } from "services/organizationPortalClient";
import contextStore from "stores/contextStore";
import { CustomDialogProps, ImportMode } from "types/commonTypes";
import { ImportUsersForm } from "./ImportUsersForm";
import { useIntl } from "react-intl";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  title: {
    fontSize: "1.2em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
});

export function ImportUsersDialog({
  open,
  onClose,
}: Readonly<CustomDialogProps>) {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { organizationUserService } = useGlobalContext();
  const { organization } = contextStore.useState();

  const [mode, setMode] = useState<ImportMode | undefined>();

  const { data } = useQuery(
    ["organizationUserService.getUsers", organization?.id],
    () =>
      organizationUserService.getUsers(
        ListRequestOfUserFilter.fromJS({ pageNumber: 0, pageSize: 1 }),
      ),
    { enabled: open },
  );

  const getDeactivateDisabledReasonId = () => {
    return data?.totalRecords === 0
      ? "organizationHasNoUsersToDeactivate"
      : undefined;
  };

  // Reset after the dialog has finished closing, not while it closes: the children stay mounted for
  // the exit transition, and clearing the mode mid-flight visibly flips the deactivation warning back
  // to the add-users access-level picker on the way out.
  return (
    <Dialog
      fullWidth
      maxWidth={"md"}
      open={open}
      TransitionProps={{ onExited: () => setMode(undefined) }}
    >
      <DialogTitle sx={styles.title}>
        {t({
          id: mode === "deactivate" ? "deactivateUsers" : "importUsers",
        })}
      </DialogTitle>
      <DialogContent>
        <ImportUsersForm
          onClose={onClose}
          mode={mode}
          onModeChange={setMode}
          deactivateDisabledReasonId={getDeactivateDisabledReasonId()}
        />
      </DialogContent>
    </Dialog>
  );
}
